/*
 * Project: bellcloud
 * File: packages/theme/theme.ts
 * Description: Theme creation using design tokens
 */

import {
  type ThemeMode,
  type ColorTokens,
  type SpacingTokens,
  type FontSize,
  type FontWeight,
  type LineHeight,
  type RadiusTokens,
  type BorderTokens,
  lightColors,
  darkColors,
  baseColors,
  spacing,
  fontSize,
  fontWeight,
  lineHeight,
  radius,
  border,
} from './tokens';

// Shadow tokens
type ShadowTokens = {
  sm: string;
  md: string;
  lg: string;
  xl: string;
};

const lightShadows: ShadowTokens = {
  sm: `0 1px 2px ${baseColors.dim.light.black}`,
  md: `0 4px 6px ${baseColors.dim.light.blackMedium}`,
  lg: `0 10px 15px ${baseColors.dim.light.blackMedium}`,
  xl: `0 20px 25px ${baseColors.dim.light.blackMedium}`,
};

const darkShadows: ShadowTokens = {
  sm: `0 1px 2px ${baseColors.dim.dark.white}`,
  md: `0 4px 6px ${baseColors.dim.dark.whiteMedium}`,
  lg: `0 10px 15px ${baseColors.dim.dark.whiteMedium}`,
  xl: `0 20px 25px ${baseColors.dim.dark.whiteMedium}`,
};

// Breakpoints for responsive design
export const breakpoints = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
};

// Z-index scale
const zIndex = {
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  modal: 1040,
  popover: 1050,
  tooltip: 1060,
};

// Theme type definition
export type AppTheme = {
  mode: ThemeMode;
  colors: ColorTokens;
  spacing: SpacingTokens;
  fontSize: FontSize;
  fontWeight: FontWeight;
  lineHeight: LineHeight;
  borderRadius: RadiusTokens;
  borderWidth: BorderTokens;
  shadows: ShadowTokens;
  breakpoints: typeof breakpoints;
  zIndex: typeof zIndex;
};

// Theme factory function
const createTheme = (mode: ThemeMode): AppTheme => ({
  mode,
  colors: mode === 'light' ? lightColors : darkColors,
  spacing,
  fontSize,
  fontWeight,
  lineHeight,
  borderRadius: radius,
  borderWidth: border,
  shadows: mode === 'light' ? lightShadows : darkShadows,
  breakpoints,
  zIndex,
});

// Pre-created themes
export const lightTheme = createTheme('light');
export const darkTheme = createTheme('dark');

export type { ThemeMode };
